import { useCallback, useEffect, useRef, useState } from 'react';
import type { DiscordGuildRole } from '@playerbase/types';

import { fetchDiscordGuildRoles } from './discord-roles-data.js';
import { supabase } from '../lib/supabase.js';

// ---------------------------------------------------------------------------
// Reads discord_guild_roles for one connection and keeps the local copy in
// sync via Realtime. Mirrors useDiscordGuildChannels - same loading
// semantics, same single-channel-per-connection subscription.
//
// Two states the caller distinguishes:
//   - loading=true on the very first load (before Realtime has fired)
//   - loading=false thereafter; refetches happen silently so the list
//     doesn't flash a placeholder on every keystroke-style change
// ---------------------------------------------------------------------------

interface UseDiscordGuildRoles {
  roles: DiscordGuildRole[];
  loading: boolean;
  reload: () => Promise<void>;
}

export function useDiscordGuildRoles(connectionId: string | null): UseDiscordGuildRoles {
  const [roles, setRoles] = useState<DiscordGuildRole[]>([]);
  const [loading, setLoading] = useState(true);

  // Track the latest connection id in a ref so the Realtime callback always
  // refetches against the connection it was opened for, not a stale closure.
  const connectionIdRef = useRef(connectionId);
  useEffect(() => {
    connectionIdRef.current = connectionId;
  }, [connectionId]);

  const reload = useCallback(async (): Promise<void> => {
    const id = connectionIdRef.current;
    if (id === null) {
      setRoles([]);
      setLoading(false);
      return;
    }
    const next = await fetchDiscordGuildRoles(id);
    setRoles(next);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (connectionId === null) {
      setRoles([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    void reload();
  }, [connectionId, reload]);

  useEffect(() => {
    if (connectionId === null) return undefined;
    const channel = supabase.channel(`discord-guild-roles:${connectionId}`);
    channel
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'discord_guild_roles',
          filter: `connection_id=eq.${connectionId}`,
        },
        () => {
          void reload();
        },
      )
      .subscribe();
    return () => {
      void channel.unsubscribe();
    };
  }, [connectionId, reload]);

  return { roles, loading, reload };
}
